import { useNavigate } from 'react-router-dom'
import { ChatStore } from '../chats/chatStore.ts'
import { VocabLessonStore } from '../vocabLessons/vocabLessonStore.ts'
import { useSnapshot } from 'valtio'
import { urls } from '../../shared/urls.ts'
import { AnimatePresence } from 'framer-motion'
import { FormattedMessage } from 'react-intl'
import {
  IMetricCard,
  lessonMetricCards,
} from '../../shared/data/progressMetrics.ts'
import { MetricKeysEnum, cn, getValueByIndex } from '../../shared/lib/utils.ts'
import MetricCard from '../../shared/ui/metricCard/metricCard.tsx'
import { BottomButtons } from '../../shared/ui/bottomButtons.tsx'
import { Spinner } from '../../shared/ui/spinner.tsx'
import {
  ADVANCED_LESSON_TAGS,
  ADVANCED_LESSON_WITH_CHAT,
} from '../../shared/data/practice.ts'

export const LessonStatistics = ({
  store,
}: {
  store: ChatStore | VocabLessonStore
}) => {
  const navigate = useNavigate()
  const { isHelpLesson, ...state } = useSnapshot(store.state)
  const metrics = state.statistics.metrics
  const isLoading = state.statistics.isLoading
  const lessonType = state.lessonType
  const isVocabLesson =
    lessonType === 'vocabulary' ||
    state.programTag === ADVANCED_LESSON_WITH_CHAT
  const programTag = state.programTag
  const isAdvancedLesson = ADVANCED_LESSON_TAGS.includes(programTag)
  const shouldGoToLearningProfile = state.shouldGoToLearningProfile

  const handleContinue = () => {
    if (shouldGoToLearningProfile) {
      navigate(urls.learningProfile())
    } else {
      navigate(isVocabLesson ? urls.practice : urls.learningProfile())
    }
    store.resetLessonStat()
  }

  const handleRepeat = () => {
    if (isAdvancedLesson) {
      navigate(urls.practiceWords('select', programTag))
    } else {
      void store.newChat(programTag, true, lessonType)
      store.resetLessonStat()
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-white px-16 sm:px-24"
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <div className="relative mx-auto flex size-full max-w-800 flex-col overflow-hidden">
        <AnimatePresence>
          <div
            className={cn('h-full', isHelpLesson ? '' : 'mt-100 msm:mt-[25%]')}
          >
            <p className="mx-auto text-center text-44 font-extrabold text-black">
              {isHelpLesson || !state.statistics.username ? (
                <FormattedMessage id="Great job" />
              ) : (
                <FormattedMessage
                  id="Great job student"
                  values={{ name: state.statistics.username }}
                />
              )}
            </p>
            {isLoading ? (
              <div className="mt-48 flex justify-center">
                <Spinner size="large" />
              </div>
            ) : (
              <>
                <p className="mx-32 mt-16 text-center text-14 text-gray4">
                  <FormattedMessage id="Tips for you to practice" />
                </p>
                <div className="mx-16 mt-32 flex h-fit min-h-136 gap-10">
                  {lessonMetricCards.map((el: IMetricCard) => {
                    const key = el.metricKey
                    const getMetricValue = (): number | '?' => {
                      if (metrics === null) return '?'

                      if (
                        key === MetricKeysEnum.UniqueWordsChat ||
                        key === MetricKeysEnum.ErrorsChat
                      ) {
                        return (
                          getValueByIndex(
                            metrics[`${key}${programTag}`],
                            0,
                          )?.value ?? '?'
                        )
                      }

                      return getValueByIndex(metrics[key], 0)?.value ?? '?'
                    }

                    return (
                      <MetricCard
                        key={key}
                        value={getMetricValue()}
                        element={el}
                      />
                    )
                  })}
                </div>
              </>
            )}
          </div>
        </AnimatePresence>
        {!isLoading && (
          <BottomButtons
            firstButtonProps={{
              handler: () => {
                handleRepeat()
              },
              textId: 'Repeat the lesson',
              bg: 'transparent',
              visible: !isHelpLesson,
            }}
            secondButtonProps={{
              handler: () => {
                handleContinue()
              },
              textId: 'Continue',
              bg: 'blue-gradient',
              visible: true,
              withIcon: !isHelpLesson,
            }}
            wrapperClassname="absolute inset-x-16 bottom-16 z-30 flex w-[calc(100%-32px)] flex-col gap-12"
          />
        )}
        {isHelpLesson && !isLoading && (
          <div className="absolute bottom-0 left-0 max-h-[50%] w-full">
            <img
              src="/images/lessonStatisticsFooter.png"
              className="h-auto max-h-[50%] w-full"
            />
            <p className="absolute inset-0 mx-32 flex items-center justify-center text-center font-nunito-7-condensed text-20">
              <FormattedMessage
                id="finish.firstStep.stat"
                values={{ br: <br />, span: <span /> }}
              />
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
